import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { HomeOperation } from '@models/operation-register';
import { TranslateService } from '@ngx-translate/core';
import { Logout } from '../core/auth/auth.actions';
import { AuthService } from '../core/auth/auth.service';
import { DeeplinkService } from '../core/deeplink.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { UiHelperService } from '../core/ui-helper.service';
import { AppLaunchable, UrlHelper } from './url-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AlertBuilder {
  private idIterator = 0;

  constructor(
    private ui: UiHelperService,
    private trans: TranslateService,
    private auth: AuthService,
    private mmobile: MMobileHelperService,
    private url: UrlHelper,
    private deeplink: DeeplinkService,
    private nav: NavigationHelper,
    private ctx: ContextService,
    private or: OperationRegisterService
  ) {}

  async showAttemptsExceededAlertLoginWithRegister(
    onRegister: () => any,
    onForgotPassword: () => any
  ) {
    const dialog = await this.ui.presentDialog(
      'Has excedido los intentos permitidos',
      `
      <br>
      <a class="dark" id="attempts-forgot-${this.idIterator}">¿Has olvidado tu contraseña?</a>
      <br><br><br>
      <span class="dark">Si aún no eres cliente, accede al formulario de registro</span>
      `,
      onRegister,
      null,
      'REGISTRATE'
    );

    // Due requirements, hack to enable function handlers inside the text message of an Ionic Alert
    const forgotPasswordBtn = document.getElementById(
      `attempts-forgot-${this.idIterator++}`
    );
    if (!forgotPasswordBtn) {
      return;
    }
    forgotPasswordBtn.addEventListener('click', () => {
      onForgotPassword();
      dialog.dismiss();
    });
  }

  async showAttemptsExceededAlertLogin(onForgotPassword: () => any) {
    const dialog = await this.ui.presentDialogOneButton(
      'Has excedido los intentos permitidos',
      `
      <br>
      <a class="dark" id="attempts-forgot-${this.idIterator}">¿Has olvidado tu contraseña?</a>
      <br><br><br>
      `,
      null,
      this.trans.instant('CANCEL')
    );

    // Due requirements, hack to enable function handlers inside the text message of an Ionic Alert
    const forgotPasswordBtn = document.getElementById(
      `attempts-forgot-${this.idIterator++}`
    );
    if (!forgotPasswordBtn) {
      return;
    }
    forgotPasswordBtn.addEventListener('click', () => {
      onForgotPassword();
      dialog.dismiss();
    });
  }

  logout() {
    this.ui.presentDialog(
      'CORE.LOGOUT.TITLE_WEB',
      'CORE.LOGOUT.TEXT_WEB',
      () => {
        const isParticular = this.ctx.isAccessTypeParticular;
        this.auth.dispatch$(new Logout()).subscribe(
          () => {
            this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
          }
        );
      },
      () => {},
      'ACCEPT',
      'CANCEL'
    );
  }

  logoutWithDisconnectMessage() {
    this.ui.presentDialog(
      'CORE.LOGOUT.TITLE_APP',
      'CORE.LOGOUT.TEXT_APP',
      () => {
        const isParticular = this.ctx.isAccessTypeParticular;
        this.auth.dispatch$(new Logout()).subscribe(
          () => {
            this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
          }
        );
      },
      () => {},
      'CORE.LOGOUT.TITLE_APP',
      'CANCEL'
    );
  }

  async installAppOrAutologin(appName: 'MAPFRE_SALUD' | 'AFIN', policy, origin?: string) {
    const isInstalled = await this.url.isInstalled(AppLaunchable[appName]);
    const autologinActive = this.mmobile.getCustomConfigData(
      'APP_ACCESS',
      appName,
      'AUTOLOGIN_ACTIVE'
    );
    if (isInstalled) {
      this.ui.presentDialog(
        this.mmobile.getCustomConfigData(
          'APP_ACCESS',
          appName,
          'INSTALLED_TITLE'
        ),
        this.mmobile.getCustomConfigData(
          'APP_ACCESS',
          appName,
          'INSTALLED_DESCRIPTION'
        ),
        () => {
          this.installAppOrAutologinOperation(
            policy,
            appName === 'MAPFRE_SALUD' ? HomeOperation.ABRIR_APP_SALUD : HomeOperation.ABRIR_APP_AFIN,
            origin
          );
          autologinActive
            ? this.deeplink.openSharedUserApp(appName)
            : this.url.openApp(AppLaunchable[appName]);
        },
        () => {},
        'OPEN_APP',
        'CANCEL'
      );
    } else {
      this.ui.presentDialog(
        this.mmobile.getCustomConfigData(
          'APP_ACCESS',
          appName,
          'UNINSTALLED_TITLE'
        ),
        this.mmobile.getCustomConfigData(
          'APP_ACCESS',
          appName,
          'UNINSTALLED_DESCRIPTION'
        ),
        () => {
          this.installAppOrAutologinOperation(
            policy,
            appName === 'MAPFRE_SALUD' ? HomeOperation.DESCARGAR_APP_SALUD : HomeOperation.DESCARGAR_APP_AFIN,
            origin
          );
          this.url.openMarket(AppLaunchable[appName]);
        },
        () => {},
        'CONTINUE',
        'CANCEL'
      );
    }
  }

  private installAppOrAutologinOperation(policy, operationCode: string, productIdName: string) {
    const { user } = this.auth.getState();
    let structure: {} = {
      nif: user?.documentNumber || '',
      name: user?.name || '',
      surname1: user?.surname1,
      surname2: user?.surname2,
      policyNumber: policy?.policyNumber || '',
      product: policy?.product || ''
    };

    switch (policy.policyBaseType) {
      case 'investment-nova':
        structure = {
          policyNumber: policy?.policyNumber || '',
          productDescription: policy?.productDescription || ''
        };
        this.or.registerInvestmentOperation(
          operationCode,
          policy,
          JSON.stringify(structure) || '',
          productIdName,
          user?.email,
          user?.documentNumber
        );
        break;
      case 'saving':
        structure = {
          nif: user?.documentNumber || '',
          product: policy.product || ''
        };
        this.or.registerLifeOperation(
          operationCode,
          policy,
          JSON.stringify(structure) || '',
          productIdName,
          user?.documentNumber
        );
        break;
      case 'pension':
        structure = {
          nif: user?.documentNumber || '',
          product: policy?.productDescription || 'CVID'
        };
        this.or.registerPensionOperation(
          operationCode,
          policy,
          JSON.stringify(structure) || '',
          productIdName
        );
        break;
      default:
        this.or.registerHealthOperation(
          operationCode,
          policy,
          JSON.stringify(structure) || '',
          productIdName,
          user?.email
        );
        break;
    }
  }
}
